import { useDataMutation } from "@lyfegen-platform/react-common";
import { Box, Typography } from "@material-ui/core";
import { Formik, useFormikContext } from "formik";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import LyfeFormField from "../../../../components/LyfeFormField";
import { localeDate } from "../../../../utils/locale-date";
import { UPDATE_DOCUMENT_PAYMENT_FIELDS } from "./queries";

const PAYMENT_REFERENCE_MAX_LENGTH = 60;
const SAVE_DEBOUNCE_MS = 1500;

export const DocumentBeneficiaryEnum = {
	Lyfegen: "Lyfegen",
	Payer: "Payer",
} as const;
export type DocumentBeneficiaryType = (typeof DocumentBeneficiaryEnum)[keyof typeof DocumentBeneficiaryEnum];

interface PaymentFieldsValues {
	beneficiary: DocumentBeneficiaryType | "";
	paymentReferenceLyfegen: string;
	paymentReceiptPayer: string | null;
}

interface RefundInvoicePaymentFieldsProps {
	documentId: string;
	isCreator: boolean;
	disabled?: boolean;
	beneficiary: DocumentBeneficiaryType | string | null | undefined;
	paymentReferenceLyfegen: string | null | undefined;
	paymentReceiptPayer: string | null | undefined;
	onSaved?: () => void;
}

const SubmitOnChange = ({
	documentId,
	isCreator,
	disabled,
	onSaved,
}: {
	documentId: string;
	isCreator: boolean;
	disabled?: boolean;
	onSaved?: () => void;
}) => {
	const { values, isValid } = useFormikContext<PaymentFieldsValues>();
	const [mutate] = useDataMutation(UPDATE_DOCUMENT_PAYMENT_FIELDS);

	useEffect(() => {
		if (!isCreator || !isValid || disabled) return;
		const payload = {
			beneficiary: values.beneficiary || null,
			paymentReferenceLyfegen: values.paymentReferenceLyfegen?.trim() || null,
			paymentReceiptPayer: values.paymentReceiptPayer || null,
		};
		const timeoutId = setTimeout(() => {
			mutate({
				variables: {
					input: {
						documentId,
						...payload,
					},
				},
				onCompleted: () => onSaved?.(),
			});
		}, SAVE_DEBOUNCE_MS);
		return () => clearTimeout(timeoutId);
	}, [values.beneficiary, values.paymentReferenceLyfegen, values.paymentReceiptPayer, isCreator, isValid, disabled, documentId, mutate, onSaved]);

	return null;
};

const RefundInvoicePaymentFields: React.FC<RefundInvoicePaymentFieldsProps> = ({
	documentId,
	isCreator,
	disabled,
	beneficiary,
	paymentReferenceLyfegen,
	paymentReceiptPayer,
	onSaved,
}) => {
	const { t } = useTranslation();

	const beneficiaryOptions = useMemo(
		() => [
			{ label: t("document-beneficiary-lyfegen"), value: DocumentBeneficiaryEnum.Lyfegen },
			{ label: t("document-beneficiary-payer"), value: DocumentBeneficiaryEnum.Payer },
		],
		[t]
	);

	const initialValues: PaymentFieldsValues = useMemo(
		() => ({
			beneficiary: (beneficiary as DocumentBeneficiaryType) ?? DocumentBeneficiaryEnum.Lyfegen,
			paymentReferenceLyfegen: paymentReferenceLyfegen ?? "",
			paymentReceiptPayer: paymentReceiptPayer ? String(paymentReceiptPayer).substring(0, 10) : null,
		}),
		[beneficiary, paymentReferenceLyfegen, paymentReceiptPayer]
	);

	const schema = Yup.object().shape({
		beneficiary: Yup.string().oneOf([DocumentBeneficiaryEnum.Lyfegen, DocumentBeneficiaryEnum.Payer]).nullable(),
		paymentReferenceLyfegen: Yup.string().max(PAYMENT_REFERENCE_MAX_LENGTH).nullable(),
		paymentReceiptPayer: Yup.date().nullable(),
	});


	if (!isCreator) {
		return (
			<Box display="flex" width="100%" flexWrap="wrap" style={{ rowGap: "24px" }} mt={2}>
				<Box width="30%" paddingX={1.5} flexShrink={0}>
					<Typography variant="caption" color="textSecondary">
						{t("document-beneficiary")}
					</Typography>
					<Typography noWrap>{beneficiary ? t(`document-beneficiary-${beneficiary.toLowerCase()}`) : "-"}</Typography>
				</Box>
				<Box width="30%" paddingX={1.5} flexShrink={0}>
					<Typography variant="caption" color="textSecondary">
						{t("document-payment-reference-lyfegen")}
					</Typography>
					<Typography noWrap>{paymentReferenceLyfegen || "-"}</Typography>
				</Box>
				<Box width="30%" paddingX={1.5} flexShrink={0}>
					<Typography variant="caption" color="textSecondary">
						{t("document-payment-receipt-payer")}
					</Typography>
					<Typography noWrap>{paymentReceiptPayer ? localeDate(paymentReceiptPayer?.substring(0, 10), { placeholder: "-" }) : "-"}</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={schema}
			validateOnMount
			validateOnChange
			enableReinitialize
			onSubmit={() => {}}
		>
			<>
				<SubmitOnChange documentId={documentId} isCreator={isCreator} disabled={disabled} onSaved={onSaved} />
				<Box display="flex" width="100%" flexWrap="wrap" style={{ rowGap: "24px" }} mt={2}>
					<Box width="30%" paddingX={1.5} flexShrink={0}>
						<LyfeFormField
							name="beneficiary"
							label={t("document-beneficiary")}
							select
							options={beneficiaryOptions}
							inputProps={{ "data-testid": "document-beneficiary-select" }}
							disabled={disabled}
						/>
					</Box>
					<Box width="30%" paddingX={1.5} flexShrink={0}>
						<LyfeFormField
							name="paymentReferenceLyfegen"
							type="text"
							label={t("document-payment-reference-lyfegen")}
							inputProps={{ maxLength: PAYMENT_REFERENCE_MAX_LENGTH }}
							disabled={disabled}
						/>
					</Box>
					<Box width="30%" paddingX={1.5} flexShrink={0}>
						<LyfeFormField
							name="paymentReceiptPayer"
							type="date"
							label={t("document-payment-receipt-payer")}
							InputProps={{ inputProps: { max: new Date().toISOString().slice(0, 10) } }}
							disabled={disabled}
						/>
					</Box>
				</Box>
			</>
		</Formik>
	);
};

export default RefundInvoicePaymentFields;
